import React, { useState, useRef, useEffect } from 'react';
import { useNotificaciones } from '../context/NotificacionesContext';
import { Modal } from './ui/Modal';

// Panel de notificaciones del usuario. Muestra el indicador de no-leídas
// debajo del nombre. Al abrir la lista, cada notificación tiene un check que
// el usuario tilda para confirmar la lectura. Al hacer clic en una notificación
// se abre un modal con el contenido completo y la imagen ampliada.
export function NotificacionesUsuarioIndicador() {
    const { notificaciones, noLeidas, loading, marcarComoLeidas } = useNotificaciones();
    const [abierto, setAbierto] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [marcandoId, setMarcandoId] = useState(null);
    const panelRef = useRef(null);

    // Cerrar dropdown al hacer clic afuera
    useEffect(() => {
        if (!abierto) return;
        const handleClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setAbierto(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [abierto]);

    const handleAbrir = () => {
        setAbierto(!abierto);
    };

    const handleMarcar = async (notificacionId) => {
        if (marcandoId !== null) return;
        setMarcandoId(notificacionId);
        await marcarComoLeidas([notificacionId]);
        setMarcandoId(null);
    };

    // Como handleMarcar pero además cierra el modal expandido al marcar.
    const handleMarcarDetalle = async (notificacionId) => {
        if (marcandoId !== null) return;
        setMarcandoId(notificacionId);
        await marcarComoLeidas([notificacionId]);
        setMarcandoId(null);
        setDetalle(null);
    };

    const abrirDetalle = (n) => {
        setDetalle(n);
        setAbierto(false);
    };

    // Notificación ampliada derivada del hook (siempre actualizada) para que el
    // checkbox de "Marcar leída" refleje el cambio al tildarlo.
    const detalleActual = detalle
        ? (notificaciones.find(x => Number(x.notificacion_id) === Number(detalle.notificacion_id)) || detalle)
        : null;

    return (
        <>
            <div className="relative" ref={panelRef}>
                {/* Botón de campanita con indicador */}
                <button
                    onClick={handleAbrir}
                    className="relative p-2 rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                    title="Notificaciones"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {noLeidas > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {noLeidas > 9 ? '9+' : noLeidas}
                        </span>
                    )}
                </button>

                {/* Dropdown con la lista */}
                {abierto && (
                    <div className="absolute right-0 mt-2 w-96 max-h-96 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50">
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Notificaciones</h3>
                            {noLeidas > 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">{noLeidas} sin leer</span>
                            )}
                        </div>
                        {loading && <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Cargando...</p>}
                        {!loading && notificaciones.length === 0 && (
                            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No tenés notificaciones.</p>
                        )}
                        {notificaciones.map(n => (
                            <div key={n.notificacion_id} className={`p-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 ${n.leida ? 'opacity-50' : ''}`}>
                                {/* Icono de expandir (arriba a la izquierda) + título */}
                                <div className="flex items-start gap-1">
                                    <button
                                        onClick={() => abrirDetalle(n)}
                                        className="flex-shrink-0 mt-0.5 p-1 rounded-md text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                                        title="Expandir / ver detalle"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                        </svg>
                                    </button>
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 flex-1">{n.titulo}</p>
                                </div>

                                {/* Contenido */}
                                <div className="pl-5">
                                    {n.descripcion && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 whitespace-pre-wrap">{n.descripcion}</p>
                                    )}
                                    {n.imagen && (
                                        <img
                                            src={n.imagen}
                                            alt={n.titulo}
                                            className="mt-2 max-h-28 rounded-md border border-gray-200 dark:border-gray-700 object-cover"
                                        />
                                    )}

                                    {/* Fecha + checkbox de leída (abajo, junto a la fecha) */}
                                    <div className="flex items-center justify-between mt-2">
                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                            {new Date(n.fecha_creacion).toLocaleString('es-AR')}
                                        </span>
                                        <button
                                            onClick={() => handleMarcar(n.notificacion_id)}
                                            disabled={n.leida || marcandoId !== null}
                                            title={n.leida ? 'Leída' : 'Marcar como leída'}
                                            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                                                n.leida
                                                    ? 'text-blue-600 dark:text-blue-400 cursor-default'
                                                    : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer'
                                            }`}
                                        >
                                            <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                                n.leida
                                                    ? 'bg-blue-500 border-blue-500 text-white'
                                                    : 'border-gray-400 dark:border-gray-500'
                                            }`}>
                                                {n.leida && (
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </span>
                                            {n.leida ? 'Leída' : 'Marcar leída'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de detalle con imagen ampliada */}
            <Modal
                isOpen={!!detalle}
                onClose={() => setDetalle(null)}
                title={detalleActual?.titulo}
                maxWidth="max-w-3xl"
            >
                {detalleActual && (
                    <div className="space-y-3">
                        {detalleActual.descripcion && (
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{detalleActual.descripcion}</p>
                        )}
                        {detalleActual.imagen && (
                            <img
                                src={detalleActual.imagen}
                                alt={detalleActual.titulo}
                                className="w-full rounded-md border border-gray-200 dark:border-gray-700"
                            />
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(detalleActual.fecha_creacion).toLocaleString('es-AR')}
                        </p>
                        {/* Marcar como leída */}
                        <div className="flex items-center justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
                            <button
                                onClick={() => detalleActual.notificacion_id && handleMarcarDetalle(detalleActual.notificacion_id)}
                                disabled={detalleActual.leida || marcandoId !== null}
                                title={detalleActual.leida ? 'Leída' : 'Marcar como leída'}
                                className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                                    detalleActual.leida
                                        ? 'text-blue-600 dark:text-blue-400 cursor-default'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer'
                                }`}
                            >
                                <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                    detalleActual.leida
                                        ? 'bg-blue-500 border-blue-500 text-white'
                                        : 'border-gray-400 dark:border-gray-500'
                                }`}>
                                    {detalleActual.leida && (
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </span>
                                {detalleActual.leida ? 'Leída' : 'Marcar leída'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}
